'use client';

import { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

import Icon from '@/components/ui/AppIcon';

const ImprintInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-secondary">
        <div className="mx-auto max-w-content px-6 lg:px-8 py-16 lg:py-20">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded-md w-1/3"></div>
            <div className="space-y-4">
              <div className="h-6 bg-muted rounded"></div>
              <div className="h-6 bg-muted rounded"></div>
              <div className="h-6 bg-muted rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-secondary relative overflow-hidden">
      <div className="mx-auto max-w-content px-6 lg:px-8 py-16 lg:py-20">
        {/* Header */}
        <div className="mb-16 text-center">
          <h1 className="font-heading text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tight">
            {t('imprint.title')}
          </h1>
          <div className="w-24 h-1 bg-gradient-gold mx-auto rounded-full" />
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - § 5 TMG */}
          <div className="bg-white rounded-2xl shadow-luxury-md p-10 lg:p-12 border border-border/50 space-y-10">
            <div className="mb-6">
              <h2 className="font-heading text-3xl font-bold text-primary mb-4 tracking-tight">
                {t('imprint.section1.title')}
              </h2>
              <div className="space-y-3 text-foreground/80 leading-relaxed">
                <p className="font-semibold text-lg text-foreground">{t('imprint.section1.company')}</p>
                <p>{t('imprint.section1.legalForm')}</p>
                <p>{t('imprint.section1.address')}</p>
                <p>{t('imprint.section1.city')}</p>
                <p>{t('imprint.section1.country')}</p>
              </div>
            </div>

            <div className="border-t border-border/30 pt-8">
              <h2 className="font-heading text-3xl font-bold text-primary mb-4 tracking-tight">
                {t('imprint.section2.title')}
              </h2>
              <div className="space-y-3 text-foreground/80 leading-relaxed">
                <div className="flex items-center gap-3">
                  <Icon name="phone" className="w-5 h-5 text-primary" />
                  <div>
                    <span className="font-medium text-foreground">{t('imprint.section2.phone')}:</span>{' '}
                    <a href="tel:+4963598676" className="text-primary hover:underline">
                      {t('imprint.section2.phoneValue')}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="phone" className="w-5 h-5 text-primary" />
                  <div>
                    <span className="font-medium text-foreground">{t('imprint.section2.mobile')}:</span>{' '}
                    <a href="tel:+491722973022" className="text-primary hover:underline">
                      {t('imprint.section2.mobileValue')}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="envelope" className="w-5 h-5 text-primary" />
                  <div>
                    <span className="font-medium text-foreground">{t('imprint.section2.email')}:</span>{' '}
                    <a href="mailto:info@golddreams.eu" className="text-primary hover:underline">
                      {t('imprint.section2.emailValue')}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-border/30 pt-8">
              <h2 className="font-heading text-3xl font-bold text-primary mb-4 tracking-tight">
                {t('imprint.section3.title')}
              </h2>
              <div className="space-y-2 text-foreground/80 leading-relaxed">
                <p>{t('imprint.section3.content')}</p>
                <p className="font-semibold text-foreground">{t('imprint.section3.vatId')}</p>
              </div>
            </div>
          </div>

          {/* Right Column - Additional Information */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-luxury-md p-10 lg:p-12 border border-border/50">
              <h2 className="font-heading text-3xl font-bold text-primary mb-4 tracking-tight">
                {t('imprint.section4.title')}
              </h2>
              <div className="space-y-3 text-foreground/80 leading-relaxed">
                <p>
                  <span className="font-medium text-foreground">{t('imprint.section4.profession')}:</span>{' '}
                  {t('imprint.section4.professionValue')}
                </p>
                <p>
                  <span className="font-medium text-foreground">{t('imprint.section4.country')}:</span>{' '}
                  {t('imprint.section4.countryValue')}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-luxury-md p-10 lg:p-12 border border-border/50">
              <h2 className="font-heading text-3xl font-bold text-primary mb-4 tracking-tight">
                {t('imprint.section5.title')}
              </h2>
              <div className="space-y-3 text-foreground/80 leading-relaxed">
                <p>{t('imprint.section5.content')}</p>
                <a
                  href={t('imprint.section5.link')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-2"
                >
                  {t('imprint.section5.link')}
                  <Icon name="arrow-top-right-on-square" className="w-4 h-4" />
                </a>
                <p className="text-sm">{t('imprint.section5.email')}</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-luxury-md p-10 lg:p-12 border border-border/50">
              <h2 className="font-heading text-3xl font-bold text-primary mb-4 tracking-tight">
                {t('imprint.section6.title')}
              </h2>
              <p className="text-foreground/80 leading-relaxed">
                {t('imprint.section6.content')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImprintInteractive;